/**
 * <Badge> — the chip (atom).
 *
 * A rounded, colored label holding a short string — a count ("3", "99+"), a status
 * ("Prêt"), a countdown ("J-3"), or a dismissible chip when `onRemove` is set.
 * Composes `<Text>` (+ optional leading `<Icon>` / trailing remove `<IconButton>`);
 * the background comes from a semantic `variant`.
 *
 * Brand core V13, chap. 6 : « Chips : hauteur 26, rayon 999, 700 / 11,5 px, letter-spacing
 * .02em. » En bas de casse, pas en capitales : la projection p. 42 écrit « Préparée » et
 * « Nouveau bilan » ainsi.
 *
 * `accent` est la chip jaune du livre (« Préparée ») — la seule surface jaune pleine du DS, et
 * elle ne s'écrit qu'en marine : « le fluo passe sur l'encre, jamais sur la feuille vide ».
 *
 * `warning` se pose sur sa tinte par défaut (`badgeFace`) : l'orange plein n'a pas d'encre lisible.
 * Dans un encart, une chip teintée (ou lavande, `neutral`) perd son fond (`hooks/useOnInset`).
 *
 * No hex literals: the variant maps to a `theme.colors.*` token.
 */
import React from "react"
import { View } from "react-native"
import { StyleSheet } from "react-native-unistyles"

import { useOnInset } from "../../hooks/useOnInset"
import { pillFillOn, type SemanticColorKey } from "../../theme/tokens/colors"
import { Icon, type IconName } from "../icons/Icon"
import { IconButton } from "../Buttons/IconButton"
import { Text } from "../typography/Text"
import { badgeFace, type BadgeVariant } from "./badgeFace"

export type { BadgeVariant } from "./badgeFace"

/** Hauteur de chip du livre — exportée pour la place qu'un squelette lui garde. */
export const CHIP_HEIGHT = 26
/** letter-spacing .02em sur 11,5 px. */
const CHIP_TRACKING = 0.23

export type BadgeProps = {
    /** Text content of the badge. */
    text: string
    /** Semantic background variant — defaults to `danger`. */
    variant?: BadgeVariant
    /**
     * Fond clair + encre foncée au lieu de l'aplat plein. Défaut : l'aplat plein, sauf `warning`,
     * qui est une tinte tant que l'appelant ne dit pas `false`.
     */
    tinted?: boolean
    /** Text (and icon) color — defaults to the variant's own (marine on a light fill, white otherwise). */
    textColor?: SemanticColorKey
    /** Optional leading glyph. */
    iconName?: IconName
    /**
     * Un glyphe de tête qui n'est pas de l'interface, à la place d'`iconName` : celui du jeu dans
     * une chip de gain (`<GainChip>`). Absent (défaut) : rien ne change.
     */
    leading?: React.ReactNode
    /** Trailing × — turns the badge into a dismissible chip. */
    onRemove?: () => void
    /** Ce que le lecteur d'écran dit du ×, quand « Retirer » ne dit pas quoi (un filtre). Défaut : « Retirer ». */
    removeLabel?: string
    /**
     * Coupe le texte au-delà de 150 px, d'une ellipse. Défaut `true`. `false` : la chip prend la
     * largeur de son texte — pour un texte que l'appelant borne déjà (`<ObjectiveHoursChip>`).
     */
    truncate?: boolean
    testID?: string
}

export function Badge(props: BadgeProps) {
    const {
        text,
        variant = "danger",
        tinted,
        textColor,
        iconName,
        leading,
        onRemove,
        removeLabel = "Retirer",
        truncate = true,
        testID,
    } = props

    const face = badgeFace(variant, tinted)
    const bg = pillFillOn(face.bg, useOnInset())
    const fg: SemanticColorKey = textColor ?? face.ink

    return (
        <View testID={testID} style={styles.badge(bg)}>
            {leading ?? (iconName != null && <Icon name={iconName} size={12} color={fg} />)}
            <Text
                size="text-xs"
                weight="bold"
                color={fg}
                numberOfLines={1}
                style={[styles.text, truncate ? styles.truncated : undefined]}
            >
                {text}
            </Text>
            {onRemove != null && (
                <IconButton
                    iconName="times"
                    appearance="transparent"
                    iconColor={fg}
                    size={20}
                    iconSize={12}
                    onPress={onRemove}
                    accessibilityLabel={removeLabel}
                />
            )}
        </View>
    )
}

const styles = StyleSheet.create((theme) => ({
    badge: (bg: SemanticColorKey | null) => ({
        flexDirection: "row" as const,
        alignItems: "center" as const,
        gap: theme.spacing["1"],
        alignSelf: "flex-start" as const,
        height: CHIP_HEIGHT,
        paddingHorizontal: theme.spacing["3"],
        borderRadius: theme.radius.full,
        backgroundColor: bg != null ? theme.colors[bg] : "transparent",
    }),
    text: {
        flexShrink: 1,
        letterSpacing: CHIP_TRACKING,
    },
    truncated: {
        maxWidth: 150,
    },
}))
